<div class="w-full max-w-full relative">
    <div class="mb-8 relative z-20">
        <div class="card-bg rounded-2xl p-6">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 class="text-2xl font-medium text-[#141437] dark:text-white">
                        User List
                    </h1>
                    <p class="text-slate-600 dark:text-slate-400 mt-1">
                        Monitor and analyze user details
                    </p>
                </div>
                <app-button variant="default" (buttonClick)="openAddUserPanel()" [iconName]="'circlePlus'"
                    className="flex items-center justify-center gap-2 shadow-[0px_6px_12px_-6px_#AD46FF] hover:shadow-[0px_4px_8px_-4px_#AD46FF] transition-all bg-[#AD46FF] hover:bg-[#AD46FF] border-none text-white">
                    <span class="font-medium whitespace-nowrap">Add User</span>
                </app-button>
            </div>
        </div>
    </div>

    <div class="mb-6 relative z-30 mx-auto">
        <div class="card-bg rounded-2xl p-6">
            <div class="flex flex-col lg:flex-row gap-4">

                <!-- Search -->
                <div class="flex-1 relative z-40">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Search
                    </label>
                    <div class="relative">
                        <div
                            class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400/80">
                            <app-icon name="search" [size]="17"></app-icon>
                        </div>
                        <input type="text" placeholder="Search name"
                            class="w-full h-[41px] pl-11 pr-4 bg-white dark:bg-surface-1 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:border-purple-500 transition-all dark:text-white placeholder:text-slate-400/50"
                            [ngModel]="searchTerm()" (input)="onSearch(searchInput.value)"
                            (keyup.enter)="applyFilters()" #searchInput>
                    </div>
                </div>

                <!-- Role -->
                <div class="flex-1 relative z-30">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Role
                    </label>
                    <app-dropdown [options]="roleOptions" [selectedValue]="selectedRoleId()" selectionMode="single"
                        placeholder="Filter by Role" [fullWidth]="true" (selectionChange)="onRoleFilter($event)"
                        class="w-full relative z-50" />
                </div>

                <!-- Status -->
                <div class="flex-1 relative z-20">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Status
                    </label>
                    <app-dropdown [options]="statusOptions" [selectedValue]="selectedStatus()" selectionMode="single"
                        placeholder="Filter by Status" [fullWidth]="true" (selectionChange)="onStatusFilter($event)"
                        class="w-full relative z-50" />
                </div>

                <div class="flex items-end z-10 gap-3">
                    <app-button variant="primary" (buttonClick)="applyFilters()"
                        className="h-[41px] px-6 text-white bg-[#AD46FF] hover:bg-[#AD46FF]/90 transition-all duration-200 shadow-md shadow-[#AD46FF]/20 rounded-xl">
                        <span class="font-medium whitespace-nowrap">Apply Filters</span>
                    </app-button>

                    <app-button [disabled]="!selectedRoleId() && !selectedStatus() && !searchTerm()" variant="secondary"
                        (buttonClick)="clearFilters()"
                        className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 rounded-xl transition-all duration-200">
                        Clear Filters
                    </app-button>
                </div>
            </div>
        </div>
    </div>

    <!-- Loading State -->
    @if (isLoading()) {
    <div
        class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg border border-white/20 dark:border-slate-700/50 rounded-2xl p-12 shadow-lg relative z-10">
        <div class="flex items-center justify-center">
            <div class="flex items-center gap-3">
                <div class="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span class="text-slate-600 dark:text-slate-400 font-medium">Loading user data...</span>
            </div>
        </div>
    </div>
    }

    <!-- Stats Table -->
    @if (!isLoading() && processedUserData.length > 0) {
    <div class="card-bg rounded-2xl overflow-hidden relative z-10">
        <!-- Table Header -->
        <div
            class="px-6 py-4 bg-gradient-to-r from-slate-50/50 to-blue-50/50 dark:from-slate-700/50 dark:to-slate-600/50 border-b border-slate-200/50 dark:border-slate-600/50">
            <h2 class="text-lg font-medium text-slate-800 dark:text-slate-200">
                User List
            </h2>
            <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {{ totalItems() }} total records found
            </p>
        </div>

        <!-- Paginated Table Component -->
        <div class="p-6">
            <app-paginated-table (actionSelected)="selectedRowItem($event)" [tableData]="processedUserData"
                [headers]="tableHeaders" [isCheckboxShow]="false" [totalItems]="totalItems()" [pageSize]="pageSize()"
                [currentPage]="page()" (pageChange)="onPageChange($event)" [serverSide]="true"
                [tableActionOptions]="{showToggleButton: true, toggleOptions:tableActionOptions}" />
        </div>
    </div>
    }

    <!-- Empty State -->
    @if (!isLoading() && processedUserData.length === 0) {
    <div
        class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg border border-white/20 dark:border-slate-700/50 rounded-2xl p-12 shadow-lg text-center relative z-10">
        <div class="max-w-md mx-auto">
            <div
                class="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            </div>
            <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                No User Data Found
            </h3>
            <p class="text-slate-600 dark:text-slate-400 mb-6">
                There are no user matching your current filters.
            </p>
            <!-- <app-button variant="primary" (buttonClick)="goToPromptCreation()"
                className="inline-flex items-center gap-2">
                Add User
            </app-button> -->
        </div>
    </div>
    }

    <!-- Error Message -->
    @if (errorMessage()) {
    <div class="mb-6">
        <app-alert [message]="errorMessage()" [duration]="5000" [type]="'error'" [closable]="true" [showProgress]="true"
            (close)="errorMessage.set('')" />
    </div>
    }

    <!-- Slide Panel -->
    <app-user-form-panel (saved)="handleUserSaved()"></app-user-form-panel>

    <!-- Audit Logs -->
    <div class="mt-12 pb-12 relative z-10">
        <app-audit-log-panel [entityType]="auditEntityTypes" [showProductFilter]="false" title="User Audit Trails">
        </app-audit-log-panel>
    </div>

</div>